import { api } from "@/lib/api";
import { ApiResponse } from "@/lib/types";
import { ExchangeRate } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";

export const useGetExchangeRatesByDataRange = (
  startDate: string,
  endDate: string
) => {
  return useQuery<ApiResponse<ExchangeRate[]>>({
    queryKey: ["exchange-rates", startDate, endDate],
    queryFn: async () => {
      const response = await api.get<ApiResponse<ExchangeRate[]>>(
        `/exchange-rates/by-date-range`,
        {
          params: {
            startDate,
            endDate,
          },
        }
      );
      return response.data;
    },
  });
};

export const useGetLatestExchangeRate = (
  fromCurrencyId: string,
  toCurrencyId: string
) => {
  return useQuery<ApiResponse<ExchangeRate>>({
    queryKey: ["exchange-rates", fromCurrencyId, toCurrencyId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<ExchangeRate>>(
        `/exchange-rates/latest`,
        {
          params: {
            fromCurrencyId,
            toCurrencyId,
          },
        }
      );
      return response.data;
    },
    enabled:
      !!fromCurrencyId && !!toCurrencyId && fromCurrencyId !== toCurrencyId,
  });
};

// Resolves the exchange rate at (or closest to) a specific date — required
// when historical accuracy matters (e.g. invoices dated in the past must
// use the rate from the issue date, not today's rate).
export const getRateAtDateQueryOptions = (
  fromCurrencyId: string,
  toCurrencyId: string,
  date: string
) => ({
  queryKey: [
    "exchange-rates",
    "at-date",
    fromCurrencyId,
    toCurrencyId,
    date,
  ] as const,
  queryFn: async (): Promise<ApiResponse<ExchangeRate>> => {
    const response = await api.get<ApiResponse<ExchangeRate>>(
      `/exchange-rates/at-date`,
      {
        params: { fromCurrencyId, toCurrencyId, date },
      }
    );
    return response.data;
  },
});

export const useGetRateAtDate = (
  fromCurrencyId: string,
  toCurrencyId: string,
  date: string
) => {
  return useQuery<ApiResponse<ExchangeRate>>({
    ...getRateAtDateQueryOptions(fromCurrencyId, toCurrencyId, date),
    enabled:
      !!fromCurrencyId &&
      !!toCurrencyId &&
      !!date &&
      fromCurrencyId !== toCurrencyId,
  });
};
